import React, { useEffect, useState } from 'react';
import SystemSearchFilters from '../../components/ActivityLog/Filtering/SystemSearchFilters';
import { SystemActivityTable } from '../../components/ActivityLog/Table/SystemActivityTable';
import { PageSpinner } from '@td-client-frontend/common';
import { format, subDays } from 'date-fns';
import { PartialActivityLogSearchQuery } from '../../@types/Audit';
import { useSystemActivities } from '../../hooks/useSystemActivities';
import { useHistory, useParams } from 'react-router-dom';
import Pagination, { ItemsPerPageOption } from '../../components/Pagination';
import Modal from '../../components/Modal/NewModal';
import ActivityDetail from '../../components/ActivityLog/ActivityDetail/ActivityDetail';

const defaultItemsPerPage = { label: '10', value: 10 };
const defaultPage = 1;
const currentDate = new Date();

const defaultSearchQuery: PartialActivityLogSearchQuery = {
  itemPerPage: defaultItemsPerPage.value,
  page: defaultPage,
  from: format(subDays(currentDate, 30), 'MM/dd/yyyy'),
  to: format(currentDate, 'MM/dd/yyyy')
};

const SystemActivityLog = () => {
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPageOption>(defaultItemsPerPage);
  const [page, setSelectedPage] = useState<number>(defaultPage);
  const [systemActivitySearchQuery, setSystemActivitySearchQuery] =
    useState<PartialActivityLogSearchQuery>(defaultSearchQuery);

  const { data, isFetching } = useSystemActivities(systemActivitySearchQuery);

  const history = useHistory();
  const closeModalHandler = () => {
    history.goBack();
  };

  const params = useParams<{ id: string }>();
  const activityId = params?.id;

  const resetPagination = () => {
    setSelectedPage(defaultPage);
    setItemsPerPage(defaultItemsPerPage);
  };

  const searchHandler = (values: PartialActivityLogSearchQuery) => {
    const notEmptyAttributes = Object.fromEntries(Object.entries(values).filter(([_, v]) => v));
    resetPagination();
    setSystemActivitySearchQuery({
      ...defaultSearchQuery,
      ...notEmptyAttributes
    });
  };
  useEffect(() => {
    setSystemActivitySearchQuery((prev) => ({ ...prev, itemPerPage: itemsPerPage.value, page }));
  }, [itemsPerPage, page]);

  return (
    <>
      {activityId && (
        <Modal
          isOpen={!!activityId}
          onClose={closeModalHandler}
          contentLabel="Activity Detail Modal"
          title="Activity Detail Modal"
          header="Activity Detail"
          size="large"
        >
          <ActivityDetail id={activityId} type="system" />
        </Modal>
      )}
      <section className="flex flex-col w-full px-6 py-4 mx-auto" data-testid="system_activity_log">
        <div className="flex flex-col space-y-7 w-full">
          <h1 className="text-4xl font-effra">System activity log</h1>
          <SystemSearchFilters onSearch={searchHandler} />
        </div>
        <div className="flex flex-col w-full mb-40 pt-2">
          {isFetching ? (
            <div className="w-full flex justify-center pt-6">
              <PageSpinner size={40} />
            </div>
          ) : !systemActivitySearchQuery.type ? (
            <p className="text-lg" data-testid="no_activity_filter_message">
              Specify the activity filter options and click search
            </p>
          ) : (
            <SystemActivityTable data={data?.records || []} selectedActivityType={systemActivitySearchQuery.type} />
          )}
          {data?.totalPages && data?.totalPages > 1 ? (
            <Pagination
              totalPages={data?.totalPages}
              onItemsPerPageChange={setItemsPerPage}
              onPageChange={setSelectedPage}
              selectedPage={page}
              itemsPerPage={itemsPerPage}
              isLoading={isFetching}
            />
          ) : null}
        </div>
      </section>
    </>
  );
};

export default SystemActivityLog;
